import { UserAPI } from 'module/api/user';
import {
  LoginWidgetLinks,
  TokenErrorStatus,
} from 'module/authentication/types';
import { getTokenMeta } from 'module/authentication/utils/decode-token';
import { useQueryParams } from 'module/helpers/location-query-params';
import { useTranslation } from 'module/hooks/use-translation';
import { Form } from 'module/ui/components/atoms/form';
import { Input } from 'module/ui/components/atoms/input/input';
import { Layout } from 'module/ui/components/atoms/layout';
import { PageLoading } from 'module/ui/components/atoms/loading';
import { BodyShort01, ProductiveHeading04 } from 'module/ui/components/styles';
import { ArrowRight } from '@carbon/icons-react';
import { Button } from '@rocketlane/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Space } from 'antd';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CompanyLogo } from '../company-logo/logo';
import { TokenError } from '../token-error';

const { Content } = Layout;

const Container = styled(Content)`
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 88px 120px;
  align-items: flex-start;
`;
const Heading = styled.div`
  ${ProductiveHeading04};
  margin-top: var(--spacing-08);
  margin-bottom: var(--spacing-03);
`;
const UserInfo = styled.div`
  ${BodyShort01};
  color: var(--coolest-gray-70);
  margin: 0 0 var(--spacing-07) 0;
`;

const LinkText = styled.span`
  ${BodyShort01};
  margin-bottom: 0;
`;

const ErrorMessage = styled.p`
  color: var(--red-60);
`;

enum PasswordsFields {
  New = 'newPassword',
  Confirm = 'confirmPassword',
}
interface Props {
  links: LoginWidgetLinks;
}
const getUserName = (user) => {
  return (
    [user?.firstName ?? '', user?.lastName ?? ''].join(' ').trim() ||
    user?.emailId
  );
};

export const ResetPassword: FC<Props> = ({ links }) => {
  const history = useNavigate();
  const params = useQueryParams();
  const urlToken = params.get('token') || '';
  const { data, isLoading } = useQuery({
    queryKey: ['get-details-from-token', urlToken],
    queryFn: () => UserAPI.getDetailsFromToken(urlToken),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const { isExpired, decoded } = getTokenMeta(urlToken);

  const updateProfile = (values) => {
    return UserAPI.resetPassword({
      newPassword: values[PasswordsFields.New] || '',
      confirmPassword: values[PasswordsFields.Confirm] || '',
      token: urlToken || '',
    })
      .then(() => {
        history(links.loginUrl);
      })
      .catch((error) => Promise.reject(error));
  };

  const passwordMatchText = useTranslation('form.validations.password_match');

  const {
    mutateAsync: sendResetMail,
    isPending: resetMailSending,
    isError,
  } = useMutation({
    mutationFn: updateProfile,
  });

  const onFinish = (values: any) => {
    sendResetMail(values);
  };

  const pwdPlaceholder = useTranslation('password.placeholder');

  if (decoded && isExpired) {
    return (
      <TokenError
        status={TokenErrorStatus.Expired}
        email={decoded.emailId}
        resendEmail={() => UserAPI.triggerResetPasswordEmail(decoded.emailId)}
        linkType={decoded.linkType}
        loginUrl={links.loginUrl}
        supportEmail={links.supportEmail}
      />
    );
  }

  if (!decoded) {
    return (
      <TokenError
        status={TokenErrorStatus.Invalid}
        loginUrl={links.loginUrl}
        supportEmail={links.supportEmail}
      />
    );
  }

  return isLoading ? (
    <PageLoading />
  ) : (
    <Layout>
      <Container>
        <Space direction="vertical" size={16}>
          {data ? (
            <CompanyLogo data={data?.sessionPayload.user.company} />
          ) : null}
          <Heading>
            <FormattedMessage
              id="reset_password.page_title"
              values={{
                user: data?.sessionPayload.user
                  ? getUserName(data?.sessionPayload.user)
                  : '',
              }}
            />
          </Heading>
          <Form layout="vertical" name="reset-password" onFinish={onFinish}>
            <Form.Item
              name={PasswordsFields.New}
              rules={[
                {
                  required: true,
                  message: (
                    <FormattedMessage id="form.validations.new_password" />
                  ),
                },
                {
                  min: 8,
                  message: <FormattedMessage id="password.help" />,
                  validateTrigger: 'onBlur',
                },
              ]}
              label={<FormattedMessage id="new_password" />}
            >
              <Input
                autoComplete="new-password"
                type="password"
                placeholder={pwdPlaceholder}
              />
            </Form.Item>
            <Form.Item
              name={PasswordsFields.Confirm}
              label={<FormattedMessage id="confirm_password" />}
              rules={[
                {
                  required: true,
                  message: (
                    <FormattedMessage id="form.validations.confirm_password" />
                  ),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }

                    return Promise.reject(passwordMatchText);
                  },
                }),
              ]}
            >
              <Input
                autoComplete="confirm-password"
                type="password"
                placeholder={pwdPlaceholder}
              />
            </Form.Item>
            <ErrorMessage>
              {isError ? 'Something went wrong. Please try again.' : ''}
            </ErrorMessage>
            <Form.Item>
              <Button
                type="submit"
                loading={resetMailSending}
                variant="primary"
                rightIcon={<ArrowRight size={16} />}
              >
                <FormattedMessage id="reset_password.cta" />
              </Button>
            </Form.Item>
            <UserInfo>
              <FormattedMessage
                id="reset_password.cta_back"
                values={{
                  a: (chunks) => (
                    <Link to={links.loginUrl}>
                      <LinkText>{chunks}</LinkText>
                    </Link>
                  ),
                }}
              />
            </UserInfo>
          </Form>
        </Space>
      </Container>
    </Layout>
  );
};
